import { LOST_STAGES } from "@/lib/constants/deal-stages";
import { ACTIVE_TICKET_STATUSES } from "@/lib/constants/ticket-statuses";
import type {
  AllocationRawData,
  AllocationUser,
  AllocationUserSkillRow,
} from "./resource-allocation-types";

function unwrapSkillName(us: AllocationUserSkillRow): string | null | undefined {
  return Array.isArray(us.skill) ? us.skill[0]?.name : us.skill?.name;
}

export function buildAllocationUsers(rawData: AllocationRawData | undefined): AllocationUser[] {
  if (!rawData) return [];

  const { users, deals, tickets, timeEntries, userSkills } = rawData;
  const openTicketStatuses: readonly string[] = ACTIVE_TICKET_STATUSES;

  const activeDeals = deals.filter(
    (d) => d.stage && !["deal_won", ...LOST_STAGES].includes(d.stage)
  );
  const openTickets = tickets.filter(
    (t) => t.status && openTicketStatuses.includes(t.status)
  );

  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay());
  weekStart.setHours(0, 0, 0, 0);
  const weekStartStr = weekStart.toISOString().split("T")[0];

  return users
    .filter((u) => u.is_billable !== false)
    .map((u): AllocationUser | null => {
      const availableHours = u.available_hours ?? 40;
      if (availableHours <= 0) return null;

      const userDeals = activeDeals.filter(
        (d) =>
          (d.assigned_users && Array.isArray(d.assigned_users) && d.assigned_users.includes(u.id)) ||
          (d.engineer_ids && Array.isArray(d.engineer_ids) && d.engineer_ids.includes(u.id))
      );
      const userTickets = openTickets.filter((t) => t.assigned_to === u.id);
      const userTimeEntries = timeEntries.filter(
        (te) => te.user_id === u.id && te.date_worked >= weekStartStr
      );

      const dealHours = userDeals.reduce((s, d) => s + (d.estimated_hours || 0), 0);
      const ticketHours = userTickets.reduce(
        (s, t) => s + (t.actual_hours || t.estimated_hours || 0),
        0
      );
      const timeEntryHours = userTimeEntries.reduce((s, te) => s + (te.hours || 0), 0);
      const allocatedHours = dealHours + ticketHours;

      const skills = userSkills
        .filter((us) => us.user_id === u.id)
        .map((us) => ({
          name: unwrapSkillName(us) || "Unknown",
          grade: us.grade || 1,
        }));

      return {
        userId: u.id,
        name: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || "Unknown",
        email: u.email || "",
        allocatedHours,
        availableHours,
        utilizationPercent: availableHours > 0 ? (allocatedHours / availableHours) * 100 : 0,
        dealCount: userDeals.length,
        ticketCount: userTickets.length,
        timeEntryHours,
        skills,
        deals: userDeals.map((d) => ({
          id: d.id,
          title: d.title || "Untitled",
          estimated_hours: d.estimated_hours || 0,
        })),
        tickets: userTickets.map((t) => ({
          id: t.id,
          title: t.title || "Untitled",
          estimated_hours: t.estimated_hours ?? t.actual_hours ?? 0,
        })),
      };
    })
    .filter((u): u is AllocationUser => u !== null)
    .sort((a, b) => a.utilizationPercent - b.utilizationPercent);
}
